import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import API from '../services/api';
import Toast from '../components/ui/Toast';
import { useApi } from '../hooks/useApi';

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    tourType: 'general'
  });
  const { data: pageContent } = useApi(() => API.content.getPageContent('contact'));
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: settings } = useApi(() => API.settings.get());

  // Get dynamic content
  const { data: content } = useApi(() => API.content.getPageContent('contact'));

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (formData.message.length < 10) {
      setToast({
        type: 'error',
        message: 'Please write a message with at least 10 characters.'
      });
      return;
    }

    setIsSubmitting(true); // ✅ ADD THIS LINE

    try {
      await API.contact.submit({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.tourType,
        message: formData.message
      });

      setToast({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        tourType: 'general'
      });
    } catch (error) {
      const errorMessage = error.message || 'Error sending message. Please try again.';
      setToast({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false); // ✅ ADD THIS LINE
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden section-header reveal-on-scroll page-top-spacing">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url("${pageContent?.hero_background?.image_url || "/contact.jpeg"}")`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/20"></div>
        </div>

        {/* Geometric overlay */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0 bg-repeat mix-blend-overlay"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zm20-16c-8.837 0-16 7.163-16 16s7.163 16 16 16 16-7.163 16-16-7.163-16-16-16z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          ></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
          <div className="section-header">
            <div className="section-overline text-gold mb-4 font-semibold tracking-wide text-sm uppercase">
              Get in Touch
            </div>
            <h1 className="section-title font-playfair text-4xl md:text-6xl font-bold mb-6 text-white">
              {pageContent?.hero_title?.content_value || 'Contact Us'}
            </h1>
          </div>
          <p className="font-graphik text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
            {pageContent?.hero_description?.content_value || 'Have questions or ready to book your ATV adventure? Reach out to us today!'}
          </p>
        </div>
      </section>

      {/* Contact Information & Form Section */}
      <section className="py-20 reveal-on-scroll bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 max-w-7xl mx-auto items-start">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="section-header">
                <div className="section-overline mb-4">
                  <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                    Contact Information
                  </span>
                </div>
                <h2 className="section-title font-playfair text-3xl md:text-4xl font-bold text-emerald-800 dark:text-white mb-6">Let's Plan Your Adventure</h2>
              </div>

              <p className="font-graphik text-lg text-gray-600 dark:text-gray-300">
                Whether you're staying at Sandals, arriving by cruise ship, or exploring from another location,
                we're here to make your ATV tour unforgettable.
              </p>

              {/* Contact Cards */}
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-emerald-100 dark:border-gray-700 reveal-on-scroll">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-playfair text-xl font-semibold text-emerald-800 dark:text-white mb-1">Phone</h3>
                      <p className="font-graphik text-gray-600 dark:text-gray-300">{settings?.contact_phone || '(614) 385-2846'}</p>
                      <p className="font-graphik text-sm text-gray-500 dark:text-gray-400 mt-1">{settings?.business_hours_mon_fri || 'Mon-Fri 9AM-5PM'} , {settings?.business_hours_saturday || 'Sat 10AM-5PM'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-emerald-100 dark:border-gray-700 reveal-on-scroll">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-playfair text-xl font-semibold text-emerald-800 dark:text-white mb-1">Email</h3>
                      <p className="font-graphik text-gray-600 dark:text-gray-300">{settings?.contact_email || 'info@layouadventures.com'}</p>
                      <p className="font-graphik text-sm text-gray-500 dark:text-gray-400 mt-1">We typically respond within 2 hours</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-emerald-100 dark:border-gray-700 reveal-on-scroll">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-playfair text-xl font-semibold text-emerald-800 dark:text-white mb-1">Location</h3>
                      <p className="font-graphik text-gray-600 dark:text-gray-300">{settings?.site_address || 'Layou Valley St. Vincent and the Grenadines'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Operating Hours */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-6 border border-emerald-100 dark:border-emerald-800/30">
                <h3 className="font-playfair text-xl font-semibold text-emerald-800 dark:text-white mb-4">Operating Hours</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-graphik text-gray-700 dark:text-gray-300">Monday - Friday</span>
                    <span className="font-graphik font-medium text-emerald-700 dark:text-emerald-400">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-graphik text-gray-700 dark:text-gray-300">Saturday</span>
                    <span className="font-graphik font-medium text-emerald-700 dark:text-emerald-400">10:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-graphik text-gray-700 dark:text-gray-300">Sunday</span>
                    <span className="font-graphik font-medium text-red-600 dark:text-red-400">Closed</span>
                  </div>
                </div>
                <p className="font-graphik text-sm text-gray-600 dark:text-gray-400 mt-3">
                  Tours run Monday through Saturday. Sunday tours are available only for Cruise-ship port packages; private group bookings require advance notice.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-emerald-100 dark:border-gray-700">
              <div className="section-header mb-4">
                <h3 className="section-title font-playfair text-xl font-bold text-emerald-800 dark:text-white">Quick Contact Form</h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="name" className="block font-graphik font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors font-graphik"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block font-graphik font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors font-graphik"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block font-graphik font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors font-graphik"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="tourType" className="block font-graphik font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tour Interest
                  </label>
                  <select
                    id="tourType"
                    name="tourType"
                    value={formData.tourType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors font-graphik"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="atv">ATV Adventure</option>
                    <option value="volcano">Volcano Hiking</option>
                    <option value="custom">Custom Package</option>
                    <option value="group">Group Booking</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block font-graphik font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    minLength="10"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors font-graphik resize-none"
                    placeholder="Tell us about your adventure interests..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-graphik font-semibold px-4 py-2 rounded-full transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="py-16 bg-emerald-800 reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="section-header text-center mb-8">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Quick Actions
              </span>
            </div>
            <h2 className="section-title font-playfair text-3xl font-bold text-white">Ready to Book?</h2>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            <Link
              to="/tours"
              className="btn-primary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Book Now</span>
            </Link>

            <Link
              to="/tours"
              className="btn-secondary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>View Tours</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
